// coordinate-dialog.component.ts
import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Coordinate } from 'src/app/shared/coordinate.model';

@Component({
    selector: 'app-coordinate-dialog',
    templateUrl: './coordinate-dialog.component.html',
    styleUrls: ['./coordinate-dialog.component.css'],
    standalone: false
})
export class CoordinateDialogComponent implements OnInit {

  title: string = '';
  fishery: string = '';
  isGroundfish: boolean = false;
  latLonLen: number;

  degrees = new FormControl('', [Validators.max(this.isLatitude ? 90 : 180)]);
  minutes = new FormControl('', [Validators.max(59)]);
  decimalMinutes = new FormControl('');
  hemisphere = new FormControl('');

  constructor(
    public dialogRef: MatDialogRef<CoordinateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { title: string, coord: Coordinate, fishery: string },
    private _focusMonitor: FocusMonitor,
    private toastr: ToastrService,
  ) {
    // Initialize latLonLen based on coordinate type
    this.latLonLen = this.data.title.toLowerCase().includes('lon') ? 3 : 2;
    this.fishery = this.data.fishery;
    this.isGroundfish = this.fishery == 'GF' || this.fishery == 'Trawl';
    if (this.isGroundfish) this.data.coord.hemisphere = this.latLonLen == 3 ? 'W' : 'N';
  }

  ngOnInit(): void {
    switch (this.data.title) {
      case 'startLat':
        this.title = 'Start Latitude';
        break;
      case 'startLon':
        this.title = 'Start Longitude';
        break;
      case 'endLat':
        this.title = 'Retrieval Latitude';
        break;
      case 'endLon':
        this.title = 'Retrieval Longitude';
        break;
    }

    let mins = Math.floor(this.data.coord.minutes || 0);
    let decMins = Math.round(((this.data.coord.minutes || 0) - mins) * 100);

    this.degrees.setValue(this.data.coord.degrees ? this.data.coord.degrees.toString() : '');
    this.minutes.setValue(mins ? mins.toString() : '');
    this.decimalMinutes.setValue(decMins ? decMins.toString().padStart(2, '0') : '');
    this.hemisphere.setValue(this.data.coord.hemisphere);

  }

  get isLatitude(): boolean {
    return this.data.title.toLowerCase().includes('lat');
  }

  _handleInput(control: AbstractControl, limit: number, nextElement?: HTMLInputElement): void {
    this._forbidNonNumeric(control);
    let next = (control.value as string).length === limit;
    if (!control.errors && nextElement && next) {
      nextElement.focus();
    }
  }

  onConfirm() {
    // If all coordinate inputs are empty, treat as a cleared coordinate.
    if (this.allCoordValuesEmpty()) {
      this.dialogRef.close(new Coordinate());
      return;
    }

    const fieldsValid = this.degrees.valid && this.minutes.valid && this.decimalMinutes.valid;
    const requiredFilled = !!this.degrees.value && !!this.minutes.value
      && (this.fishery !== 'HMS' || !!this.hemisphere.value);

    if (fieldsValid && requiredFilled) {
      let coord = new Coordinate();
      coord.degrees = Number.parseInt(this.degrees.value!);
      coord.minutes = Number.parseFloat(this.minutes.value! + '.' + (this.decimalMinutes.value || '0'));
      coord.hemisphere = this.hemisphere.value!;
      this.dialogRef.close(coord);
    } else {
      this.toastr.error("Enter a valid coordinate", "Invalid Coordinate");
    }
  }

  onClear() {
    this.degrees.setValue('');
    this.minutes.setValue('');
    this.decimalMinutes.setValue('');
    if (this.fishery === 'HMS') {
      this.hemisphere.setValue('');
    }
    [this.degrees, this.minutes, this.decimalMinutes, this.hemisphere].forEach(c => {
      c.markAsPristine();
      c.markAsUntouched();
    });
  }

  private allCoordValuesEmpty(): boolean {
    return !this.degrees.value && !this.minutes.value && !this.decimalMinutes.value;
  }

  _forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), { onlySelf: true, emitEvent: false });
  }

}
